<template>
    <eSesjaModal class="visualization-activity">
        <eSesjaModalHeader @close="close">
            {{$t('com.file.visualization-link-activity.title')}}
        </eSesjaModalHeader>
        <eSesjaModalBody class="d-flex flex-column gap-2">
            <p class="activity-info">{{$t('com.file.visualization-link-activity.info')}}</p>
            <p class="activity-slug" v-if="slug">{{slug}}</p>

            <InlineLoader v-if="isLoading" />

            <p class="activity-info" v-else-if="totalItems === 0">
                {{$t('com.file.visualization-link-activity.empty')}}
            </p>

            <template v-else>
                <table class="activity">
                    <thead>
                        <tr>
                            <th>{{$t('com.file.visualization-link-activity.column.time')}}</th>
                            <th>{{$t('com.file.visualization-link-activity.column.event')}}</th>
                            <th>{{$t('com.file.visualization-link-activity.column.address')}}</th>
                            <th>{{$t('com.file.visualization-link-activity.column.ip')}}</th>
                            <th>{{$t('com.file.visualization-link-activity.column.details')}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(entry, index) in entries" :key="index">
                            <td class="activity__time">{{formatDate(entry.createdAt)}}</td>
                            <td>{{translateEvent(entry.eventType)}}</td>
                            <td class="activity__slug">{{entry.slug}}</td>
                            <td class="activity__ip">{{entry.ipAddress}}</td>
                            <td class="activity__details" :title="entry.userAgent">{{entry.details}}</td>
                        </tr>
                    </tbody>
                </table>

                <Pagination :total-items="totalItems"
                            :current-page="page"
                            :items-per-page="pageSize"
                            @goToPage="goToPage" />
            </template>
        </eSesjaModalBody>
        <eSesjaModalFooter>
            <eSesjaModalButtonCancel @close="close">
                {{ $t('com.global.close') }}
            </eSesjaModalButtonCancel>
            <eSesjaModalButtonSave @save="openExport" :disabled="totalItems === 0">
                {{$t('com.file.visualization-link-activity.export')}}
            </eSesjaModalButtonSave>
        </eSesjaModalFooter>
    </eSesjaModal>
</template>
<script charset="utf-8">
    import moment from "moment";
    import SettingsService from "@/services/settings.service";
    import InlineLoader from "@/components/BasicControls/InlineLoader.vue";
    import Pagination from "@/components/BasicControls/Pagination.vue";
    import { toTranslationKeySuffix } from "@/helpers/ow.route.helper";

    const PAGE_SIZE = 25;

    export default {
        name: "VisualizationLinkActivityModal",
        components: {
            InlineLoader,
            Pagination
        },
        props: {
            /**
             * Adres, którego dotyczy historia - pokazywany w nagłówku, żeby było jasne, czego są te zdarzenia.
             */
            slug: {
                type: String,
                default: ""
            }
        },
        data() {
            return {
                isLoading: false,
                entries: [],
                totalItems: 0,
                page: 1,
                pageSize: PAGE_SIZE
            }
        },
        async mounted() {
            await this.getActivity();
        },
        methods: {
            async getActivity() {
                try {
                    this.isLoading = true;

                    const response = await SettingsService.getVisualizationLinkActivity(this.page, this.pageSize);
                    const data = response.status === 200 ? response.data : null;

                    this.entries = data !== null && Array.isArray(data.entries) ? data.entries : [];
                    this.totalItems = data !== null ? (data.totalItems || 0) : 0;
                    this.pageSize = data !== null && data.pageSize > 0 ? data.pageSize : PAGE_SIZE;
                }
                catch {
                    this.entries = [];
                    this.totalItems = 0;
                }
                finally {
                    this.isLoading = false;
                }
            },
            async goToPage(page) {
                this.page = page;
                await this.getActivity();
            },
            openExport() {
                this.$store.commit("setVisualizationLinkActivityExportModalStatus", true);
            },
            close() {
                this.$store.commit("setVisualizationLinkActivityModalStatus", false);
            },
            formatDate(date) {
                return moment(date).format("YYYY-MM-DD HH:mm:ss");
            },
            /**
             * Serwer zwraca nazwę wartości enuma VisualizationActivityEvent - nieznana nazwa (np. rodzaj
             * dodany po stronie serwera, zanim doszły tłumaczenia) zostaje pokazana wprost, zamiast klucza.
             */
            translateEvent(eventType) {
                const key = `com.file.visualization-link-activity.event.${toTranslationKeySuffix(eventType)}`;
                const message = this.$t(key);

                return message === key ? eventType : message;
            }
        }
    }
</script>
<style lang="scss" scoped>
    /*
     * Prefiks w nazwach klas jest konieczny: IntegrationImportantDataSettings (ten sam ekran ustawień)
     * ma niescopowany blok stylów z regułą .info { text-align: center; ... }, która wyciekała tutaj.
     */
    .activity-info {
        font-size: 13px;
        color: #666c7a;
        margin: 0px;
        text-align: left;
    }

    .activity-slug {
        font-size: 13px;
        font-weight: 600;
        color: #00082c;
        margin: 0px;
        word-break: break-all;
        text-align: left;
    }

    /*
     * Odstęp musi być na stopce, a nie na końcu treści: treść jest przewijana (max-height 70vh),
     * więc margines w jej wnętrzu widać dopiero po dojechaniu na sam dół, a przyciski i tak
     * przylegały do tabeli.
     */
    :deep(.esesja-modal__wraper__body__bottom) {
        padding-top: 20px;
    }

    .activity {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        color: #00082c;

        th {
            text-align: left;
            font-weight: 600;
            padding: 6px 12px 6px 0px;
            border-bottom: 1px solid #dce4ee;
            white-space: nowrap;
        }

        td {
            padding: 6px 12px 6px 0px;
            border-bottom: 1px solid #f0f3f8;
            vertical-align: top;
        }

        &__time,
        &__ip {
            white-space: nowrap;
        }

        &__slug,
        &__details {
            word-break: break-all;
        }
    }
</style>
